import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { PDFDownloadLink } from '@react-pdf/renderer'
import InspectionPDF from './InspectionPDF'
import { Badge } from './UI'
import { useTheme } from '../ThemeContext'
import { useOrganization } from '../hooks/useOrganization'
import { useAuth } from '../AuthContext'
import EditInspectionModal from './EditInspectionModal'
import toast from 'react-hot-toast'
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLog'
import { SignedImg } from '../utils/photoUrls'

export default function InspectionHistory({ establishment, onClose, onNewInspection, onEdit, onArchive, onRestore, onDelete, showArchived, onPriorityCleared }) {
  const { darkMode } = useTheme()
  const { organization } = useOrganization()
  const { user } = useAuth()
  const idLabel = organization?.settings?.identifier_label || 'MXB Number'

  const mGlassBg     = darkMode ? 'rgba(15,30,50,0.97)'   : 'rgba(255,255,255,0.98)'
  const mGlassBorder = darkMode ? 'rgba(90,127,173,0.18)' : 'rgba(61,100,148,0.13)'
  const mGlassShadow = darkMode ? '0 24px 64px rgba(0,0,0,0.5)' : '0 24px 64px rgba(30,58,100,0.14)'
  const mPrimary     = darkMode ? '#c8daf5' : '#1a2a3e'
  const mSecondary   = darkMode ? '#7aa0c8' : '#4a6080'
  const mAccent      = darkMode ? '#7aa0c8' : '#3d6494'
  const mRowBorder   = darkMode ? 'rgba(90,127,173,0.12)' : 'rgba(61,100,148,0.1)'
  const mTheadBg     = darkMode ? 'rgba(90,127,173,0.08)' : 'rgba(61,100,148,0.05)'
  const mRowHover    = darkMode ? 'rgba(90,127,173,0.1)'  : 'rgba(61,100,148,0.06)'
  const [inspections, setInspections] = useState([])
  // Phones never show the establishments table, where Edit and Archive live on desktop
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedInspection, setSelectedInspection] = useState(null)
  const [editingInspection, setEditingInspection] = useState(null)

  const [activeTab, setActiveTab] = useState('inspections')
  const [fieldNotes, setFieldNotes] = useState([])
  const [notesLoading, setNotesLoading] = useState(false)
  const [newNoteText, setNewNoteText] = useState('')
  const [submittingNote, setSubmittingNote] = useState(false)
  const [currentUserId, setCurrentUserId] = useState(null)
  const [currentUserRole, setCurrentUserRole] = useState(null)
  const [isPriority, setIsPriority] = useState(!!establishment.priority_inspection)
  const [clearingPriority, setClearingPriority] = useState(false)

  const handleClearPriority = async () => {
    setClearingPriority(true)
    const { error } = await supabase
      .from('establishments')
      .update({ priority_inspection: false, priority_reason: null, priority_set_at: null, priority_set_by: null })
      .eq('id', establishment.id)
    setClearingPriority(false)
    if (error) { toast.error('Failed to clear priority flag'); return }
    setIsPriority(false)
    logAuditEvent(AUDIT_ACTIONS.PRIORITY_CLEARED, { establishment_id: establishment.id, mxb_number: establishment.mxb_number, trade_name: establishment.trade_name }).catch(() => {})
    toast.success('Priority flag removed')
    onPriorityCleared?.()
  }

  const fetchInspections = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('inspections')
        .select(`
          *,
          agent:inspector_id(first_name, last_name)
        `)
        .eq('establishment_id', establishment.id)
        .order('inspection_date', { ascending: false })

      if (error) throw error
      setInspections(data || [])
    } catch (error) {
      console.error('Error fetching inspections:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }, [establishment.id])

  useEffect(() => {
    if (establishment) {
      fetchInspections()
    }
  }, [establishment, fetchInspections])

  useEffect(() => {
    if (!user?.email) return
    supabase.from('users').select('id, role').eq('email', user.email).single()
      .then(({ data }) => {
        if (data) {
          setCurrentUserId(data.id)
          setCurrentUserRole(data.role)
        }
      })
  }, [user])

  const fetchNotes = useCallback(async () => {
    setNotesLoading(true)
    try {
      const { data, error } = await supabase
        .from('establishment_notes')
        .select('id, note, created_at, created_by:created_by(first_name, last_name, id)')
        .eq('establishment_id', establishment.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      setFieldNotes(data || [])
    } catch (err) {
      console.error('Error fetching field notes:', err)
    } finally {
      setNotesLoading(false)
    }
  }, [establishment.id])

  useEffect(() => {
    if (activeTab === 'notes') fetchNotes()
  }, [activeTab, fetchNotes])

  const handleAddNote = async () => {
    const text = newNoteText.trim()
    if (!text || !currentUserId || !organization?.id) return
    setSubmittingNote(true)
    try {
      const { error } = await supabase.from('establishment_notes').insert({
        establishment_id: establishment.id,
        organization_id: organization.id,
        note: text,
        created_by: currentUserId
      })
      if (error) throw error
      setNewNoteText('')
      fetchNotes()
    } catch (err) {
      console.error('Error adding note:', err)
    } finally {
      setSubmittingNote(false)
    }
  }

  const handleDeleteNote = async (noteId) => {
    const { error } = await supabase.from('establishment_notes').delete().eq('id', noteId)
    if (!error) setFieldNotes(prev => prev.filter(n => n.id !== noteId))
  }


  const formatDate = (dateString) => {
    // Parse date as local date to avoid timezone conversion issues
    // dateString format is "YYYY-MM-DD"
    const [year, month, day] = dateString.split('-')
    const date = new Date(year, month - 1, day) // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getDispositionBadge = (disposition) => {
    // Handle NULL/undefined disposition
    if (!disposition) {
      return <Badge variant="never" size="sm">N/A</Badge>
    }

    const labels = {
      in_compliance: 'In Compliance',
      needs_attention: 'Needs Attention',
      warning: 'Warning',
      violation: 'Violation',
      new_opening: 'New Opening'
    }

    const variantMap = {
      in_compliance: 'current',
      needs_attention: 'dueSoon',
      warning: 'pending',
      violation: 'overdue',
      new_opening: 'newOpening'
    }

    return (
      <Badge variant={variantMap[disposition] || 'current'} size="sm">
        {labels[disposition] || 'In Compliance'}
      </Badge>
    )
  }

  const formatValue = (value) => {
    if (!value) return 'N/A'
    if (value === 'yes') return 'Yes'
    if (value === 'no') return 'No'
    if (value === 'n/a') return 'N/A'
    if (value === 'in_compliance') return 'In Compliance'
    if (value === 'needs_attention') return 'Needs Attention'
    if (value === 'not_in_compliance') return 'Not in Compliance'
    return value
  }

  const handleNewInspection = () => {
    onClose()
    onNewInspection(establishment)
  }

  const sectionLabel = (text) => (
    <div style={{ fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: mAccent, marginBottom: '0.5rem', marginTop: '1rem', paddingTop: '0.75rem', borderTop: `1px solid ${mRowBorder}` }}>
      {text}
    </div>
  )

  const fieldLabel = { color: mSecondary, fontSize: '0.75rem', whiteSpace: 'nowrap' }
  const fieldValue = { marginLeft: '0.4rem', fontWeight: '600', color: mPrimary, fontSize: '0.82rem', wordBreak: 'break-word', overflowWrap: 'anywhere' }

  const InspectionDetail = ({ inspection }) => (
    <div style={{ marginTop: '0.75rem', backgroundColor: mTheadBg, border: `1px solid ${mRowBorder}`, borderRadius: '10px', padding: '1rem', overflowX: 'hidden', minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <div style={{ fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: mAccent, marginBottom: '0.2rem' }}>Inspection Detail</div>
          <div style={{ fontWeight: '700', fontSize: '1rem', color: mPrimary }}>{formatDate(inspection.inspection_date)}</div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => setEditingInspection(inspection)}
            style={{ padding: '0.45rem 1rem', backgroundColor: mTheadBg, color: mSecondary, border: `1px solid ${mRowBorder}`, borderRadius: '9999px', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}
          >
            Edit
          </button>
          <PDFDownloadLink
            document={<InspectionPDF inspection={inspection} establishment={establishment} idLabel={idLabel} />}
            fileName={`inspection-${establishment.mxb_number}-${inspection.inspection_date}.pdf`}
            style={{ padding: '0.45rem 1rem', backgroundColor: mAccent, color: 'white', textDecoration: 'none', borderRadius: '9999px', fontSize: '0.8rem', fontWeight: '600', boxShadow: '0 2px 8px rgba(61,100,148,0.3)' }}
          >
            {({ loading }) => (loading ? 'Generating...' : '📄 Download PDF')}
          </PDFDownloadLink>
          <button
            onClick={() => setSelectedInspection(null)}
            style={{ background: mTheadBg, border: `1px solid ${mRowBorder}`, fontSize: '1rem', cursor: 'pointer', color: mSecondary, width: '30px', height: '30px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            ✕
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.5rem', fontSize: '0.82rem' }}>
        <div>
          <span style={fieldLabel}>Agent:</span>
          <span style={fieldValue}>{inspection.agent ? `${inspection.agent.first_name} ${inspection.agent.last_name}` : 'Unknown'}</span>
        </div>
        <div>
          <span style={fieldLabel}>Disposition:</span>
          <span style={{ marginLeft: '0.4rem' }}>{getDispositionBadge(inspection.disposition)}</span>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>{sectionLabel('NC ABC Permits')}</div>
        <div><span style={fieldLabel}>Mixed Beverage:</span><span style={fieldValue}>{formatValue(inspection.mixed_beverage)}</span></div>
        <div><span style={fieldLabel}>Beer On-Premises:</span><span style={fieldValue}>{formatValue(inspection.beer_on)}</span></div>
        <div><span style={fieldLabel}>Beer Off-Premises:</span><span style={fieldValue}>{formatValue(inspection.beer_off)}</span></div>
        <div><span style={fieldLabel}>Unfortified Wine:</span><span style={fieldValue}>{formatValue(inspection.unfortified_wine)}</span></div>
        <div><span style={fieldLabel}>Fortified Wine:</span><span style={fieldValue}>{formatValue(inspection.fortified_wine)}</span></div>
        {inspection.other_state_permits && (
          <div style={{ gridColumn: '1 / -1' }}><span style={fieldLabel}>Other State Permits:</span><span style={fieldValue}>{inspection.other_state_permits}</span></div>
        )}

        <div style={{ gridColumn: '1 / -1' }}>{sectionLabel('Local Licenses')}</div>
        <div><span style={fieldLabel}>County License:</span><span style={fieldValue}>{formatValue(inspection.county_license)}</span></div>
        <div><span style={fieldLabel}>City License:</span><span style={fieldValue}>{formatValue(inspection.city_license)}</span></div>

        <div style={{ gridColumn: '1 / -1' }}>{sectionLabel('Records')}</div>
        <div><span style={fieldLabel}>Alcohol Records:</span><span style={fieldValue}>{formatValue(inspection.alcohol_records)}</span></div>
        <div><span style={fieldLabel}>Financial Records:</span><span style={fieldValue}>{formatValue(inspection.financial_records)}</span></div>

        <div style={{ gridColumn: '1 / -1' }}>{sectionLabel('Premises')}</div>
        <div><span style={fieldLabel}>Storage Area:</span><span style={fieldValue}>{formatValue(inspection.storage_area)}</span></div>
        <div><span style={fieldLabel}>Advertising:</span><span style={fieldValue}>{formatValue(inspection.advertising)}</span></div>
        <div><span style={fieldLabel}>Cleanliness:</span><span style={fieldValue}>{formatValue(inspection.cleanliness)}</span></div>

        {inspection.person_in_charge && (
          <>
            <div style={{ gridColumn: '1 / -1' }}>{sectionLabel('Person in Charge')}</div>
            <div style={{ gridColumn: '1 / -1' }}><span style={fieldValue}>{inspection.person_in_charge}</span></div>
          </>
        )}

        {inspection.comments && (
          <>
            <div style={{ gridColumn: '1 / -1' }}>{sectionLabel('Comments')}</div>
            <div style={{ gridColumn: '1 / -1', padding: '0.625rem 0.875rem', backgroundColor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.7)', borderRadius: '8px', border: `1px solid ${mRowBorder}`, whiteSpace: 'pre-wrap', color: mPrimary, fontSize: '0.82rem' }}>
              {inspection.comments}
            </div>
          </>
        )}

        {inspection.photo_urls && inspection.photo_urls.length > 0 && (
          <>
            <div style={{ gridColumn: '1 / -1' }}>{sectionLabel('Photos')}</div>
            <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '0.625rem' }}>
              {inspection.photo_urls.map((url, index) => (
                <SignedImg key={index} src={url} alt={`Inspection ${index + 1}`}
                  style={{ width: '100%', height: '130px', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${mRowBorder}` }} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(10,20,35,0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '0.5rem'
    }}>
      <div style={{
        backgroundColor: mGlassBg,
        border: `1px solid ${mGlassBorder}`,
        boxShadow: mGlassShadow,
        borderRadius: '14px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '95vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: `1px solid ${mRowBorder}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div style={{ flex: 1, paddingRight: '1rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.12em', textTransform: 'uppercase', color: mAccent, marginBottom: '0.25rem' }}>
              Establishment
            </div>
            <h2 style={{ margin: '0 0 0.3rem 0', fontSize: 'clamp(1rem, 3.5vw, 1.35rem)', fontWeight: '700', color: mPrimary }}>{establishment.trade_name}</h2>
            <p style={{ margin: 0, color: mSecondary, fontSize: '0.8rem' }}>
              {establishment.mxb_number} &nbsp;·&nbsp; {establishment.address}, {establishment.city}, NC {establishment.zip}
            </p>
            {establishment.notes && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.625rem 0.875rem',
                backgroundColor: darkMode ? 'rgba(180,120,0,0.15)' : '#fef3c7',
                border: '1px solid rgba(251,191,36,0.3)',
                borderLeft: '3px solid #f59e0b',
                borderRadius: '6px',
              }}>
                <p style={{ margin: '0 0 0.2rem 0', fontWeight: '700', fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: darkMode ? '#fbbf24' : '#92400e' }}>Notes</p>
                <p style={{ margin: 0, fontSize: '0.82rem', color: darkMode ? '#fde68a' : '#78350f', whiteSpace: 'pre-wrap' }}>
                  {establishment.notes}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: mTheadBg, border: `1px solid ${mRowBorder}`,
              fontSize: '1.1rem', cursor: 'pointer', color: mSecondary,
              width: '32px', height: '32px', borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, transition: 'all 0.15s'
            }}
          >
            ×
          </button>
        </div>

        {/* Action Buttons */}
        <div style={{
          padding: '0.75rem 1.25rem',
          borderBottom: `1px solid ${mRowBorder}`,
          backgroundColor: mTheadBg
        }}>
          <button
            onClick={handleNewInspection}
            style={{
              width: '100%', padding: '0.65rem 1.5rem',
              backgroundColor: mAccent, color: 'white',
              border: 'none', borderRadius: '9999px',
              fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(61,100,148,0.35)',
              transition: 'all 0.15s'
            }}
          >
            + New Inspection
          </button>
          {isMobile && (onEdit || onArchive) && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              {[
                // Close this window first so the edit form or archive confirmation is not hidden behind it
                onEdit && { label: 'Edit', run: e => { onClose(); onEdit(establishment, e) } },
                onArchive && establishment?.active !== false && { label: 'Archive', run: e => { onClose(); onArchive(establishment, e) } },
              ].filter(Boolean).map(({ label, run }) => (
                <button
                  key={label}
                  onClick={run}
                  style={{
                    flex: 1, minHeight: '44px', padding: '0 1rem',
                    backgroundColor: 'transparent', color: mAccent,
                    border: `1px solid ${mRowBorder}`, borderRadius: '9999px',
                    fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {isPriority && (
            <button
              onClick={handleClearPriority}
              disabled={clearingPriority}
              style={{
                width: '100%', marginTop: '8px', padding: '0.55rem 1.5rem',
                backgroundColor: 'transparent', color: '#b45309',
                border: '1px solid rgba(245,158,11,0.5)', borderRadius: '9999px',
                fontSize: '0.85rem', fontWeight: '600', cursor: clearingPriority ? 'default' : 'pointer',
                opacity: clearingPriority ? 0.6 : 1,
                transition: 'all 0.15s'
              }}
            >
              {clearingPriority ? 'Removing…' : '⚑ Remove Priority Flag'}
            </button>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${mRowBorder}`, padding: '0 1.25rem', backgroundColor: mGlassBg }}>
          {['inspections', 'notes'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.6rem 1rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: '600',
                color: activeTab === tab ? mAccent : mSecondary,
                borderBottom: activeTab === tab ? `2px solid ${mAccent}` : '2px solid transparent',
                marginBottom: '-1px',
                transition: 'all 0.15s',
                textTransform: 'capitalize'
              }}
            >
              {tab === 'inspections' ? 'Inspection History' : 'Field Notes'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          padding: '1rem 1.25rem',
          overflowY: 'auto',
          flex: 1
        }}>
          {activeTab === 'inspections' && (
          <div style={{ fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', color: mAccent, marginBottom: '0.75rem' }}>
            Inspection History
          </div>
          )}

          {activeTab === 'inspections' && (<>
          {loading && (
            <div style={{ textAlign: 'center', padding: '2rem', color: mSecondary, fontSize: '0.875rem' }}>
              Loading inspections...
            </div>
          )}

          {error && (
            <div style={{
              backgroundColor: darkMode ? 'rgba(153,27,27,0.2)' : '#fef2f2',
              color: darkMode ? '#fca5a5' : '#991b1b',
              padding: '0.875rem', borderRadius: '8px',
              border: '1px solid rgba(220,38,38,0.2)', fontSize: '0.85rem'
            }}>
              Error: {error}
            </div>
          )}

          {!loading && !error && inspections.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', color: mSecondary }}>
              <p style={{ fontSize: '1rem', marginBottom: '0.5rem', color: mPrimary }}>No inspections recorded yet</p>
              <p style={{ fontSize: '0.85rem' }}>Click the button above to create the first inspection.</p>
            </div>
          )}

          {!loading && !error && inspections.length > 0 && !selectedInspection && (
            <div>
              <p style={{ marginTop: 0, marginBottom: '0.75rem', color: mSecondary, fontSize: '0.8rem' }}>
                {inspections.length} inspection{inspections.length !== 1 ? 's' : ''}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {inspections.map((inspection) => (
                  <div
                    key={inspection.id}
                    onClick={() => setSelectedInspection(inspection)}
                    style={{
                      padding: '0.75rem 1rem',
                      backgroundColor: mTheadBg,
                      border: `1px solid ${mRowBorder}`,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = mRowHover}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = mTheadBg}
                  >
                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <p style={{ margin: '0 0 0.2rem 0', fontWeight: '600', fontSize: '0.9rem', color: mPrimary }}>
                        {formatDate(inspection.inspection_date)}
                      </p>
                      <p style={{ margin: 0, color: mSecondary, fontSize: '0.78rem' }}>
                        {inspection.agent ? `${inspection.agent.first_name} ${inspection.agent.last_name}` : 'Unknown'}
                      </p>
                      {inspection.comments && (
                        <p style={{ margin: '0.3rem 0 0 0', fontSize: '0.78rem', color: mSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {inspection.comments}
                        </p>
                      )}
                    </div>
                    <div style={{ flexShrink: 0, marginLeft: '0.5rem' }}>{getDispositionBadge(inspection.disposition)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedInspection && (
            <InspectionDetail inspection={selectedInspection} />
          )}
          </>)}

          {activeTab === 'notes' && (
            <div>
              {/* Add Note Form */}
              <div style={{ marginBottom: '1.25rem' }}>
                <textarea
                  value={newNoteText}
                  onChange={e => setNewNoteText(e.target.value)}
                  placeholder="Add a field note about this establishment..."
                  rows={3}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '0.75rem', borderRadius: '8px',
                    border: `1px solid ${mRowBorder}`,
                    backgroundColor: mTheadBg,
                    color: mPrimary, fontSize: '0.875rem',
                    resize: 'vertical', fontFamily: 'inherit',
                    outline: 'none'
                  }}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddNote() }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                  <button
                    onClick={handleAddNote}
                    disabled={!newNoteText.trim() || submittingNote}
                    style={{
                      padding: '0.5rem 1.25rem',
                      backgroundColor: !newNoteText.trim() || submittingNote ? mRowBorder : mAccent,
                      color: 'white', border: 'none', borderRadius: '9999px',
                      fontSize: '0.82rem', fontWeight: '600',
                      cursor: !newNoteText.trim() || submittingNote ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    {submittingNote ? 'Saving...' : 'Save Note'}
                  </button>
                </div>
              </div>

              {/* Notes List */}
              {notesLoading && (
                <div style={{ textAlign: 'center', padding: '1.5rem', color: mSecondary, fontSize: '0.85rem' }}>Loading notes...</div>
              )}
              {!notesLoading && fieldNotes.length === 0 && (
                <div style={{ textAlign: 'center', padding: '2.5rem', color: mSecondary }}>
                  <p style={{ margin: '0 0 0.35rem 0', fontSize: '0.95rem', color: mPrimary }}>No field notes yet</p>
                  <p style={{ margin: 0, fontSize: '0.82rem' }}>Notes are visible to all agents and supervisors in your organization.</p>
                </div>
              )}
              {!notesLoading && fieldNotes.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                  {fieldNotes.map(note => {
                    const authorName = note.created_by ? `${note.created_by.first_name} ${note.created_by.last_name}` : 'Unknown'
                    const canDelete = currentUserRole === 'admin' || currentUserRole === 'super_admin' || note.created_by?.id === currentUserId
                    const ts = new Date(note.created_at)
                    const formattedDate = ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    const formattedTime = ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                    return (
                      <div key={note.id} style={{
                        padding: '0.75rem 1rem',
                        backgroundColor: mTheadBg,
                        border: `1px solid ${mRowBorder}`,
                        borderRadius: '10px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
                          <div>
                            <span style={{ fontWeight: '700', fontSize: '0.8rem', color: mPrimary }}>{authorName}</span>
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: mSecondary }}>{formattedDate} at {formattedTime}</span>
                          </div>
                          {canDelete && (
                            <button
                              onClick={() => handleDeleteNote(note.id)}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: mSecondary, fontSize: '0.75rem', padding: '0 0.25rem',
                                opacity: 0.6, transition: 'opacity 0.15s'
                              }}
                              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                              onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}
                              title="Delete note"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: mPrimary, whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                          {note.note}
                        </p>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Inspection Modal */}
      {editingInspection && (
        <EditInspectionModal
          inspection={editingInspection}
          establishment={establishment}
          onClose={() => setEditingInspection(null)}
          onSuccess={() => {
            setEditingInspection(null)
            setSelectedInspection(null)
            fetchInspections()
          }}
        />
      )}
    </div>
  )
}